(() => {
  const PRODUCT_SELECTOR = "[data-ap-product]";
  const PYRAMID_SELECTOR = "[data-ap-pyramid]";

  function parseVariants(root) {
    const script = root.querySelector("[data-ap-variants]");
    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error("Aromatica product variants could not be parsed.", error);
      return [];
    }
  }

  function selectedOptions(root) {
    return Array.from(
      root.querySelectorAll("[data-ap-option-value]:checked"),
    ).map((input) => input.value);
  }

  function normalizeOption(value) {
    return String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");
  }

  function findVariant(root, variants) {
    const selectedInput = root.querySelector(
      "[data-ap-option-value]:checked[data-variant-id]",
    );
    if (selectedInput?.dataset.variantId) {
      const directVariant = variants.find(
        (variant) =>
          String(variant.id) === String(selectedInput.dataset.variantId),
      );
      if (directVariant) return directVariant;
    }

    const options = selectedOptions(root);
    if (!options.length) return variants[0];

    return variants.find(
      (variant) =>
        variant.options.length === options.length &&
        variant.options.every(
          (option, index) =>
            normalizeOption(option) === normalizeOption(options[index]),
        ),
    );
  }

  function normalizedUrl(value) {
    try {
      return new URL(value, document.baseURI).href;
    } catch (_error) {
      return value || "";
    }
  }

  function setGalleryImage(root, source, fallbackAlt = "") {
    const gallery = root.querySelector("[data-ap-gallery]");
    const mainImage = gallery?.querySelector(".ap-pdp__image--primary");
    if (!gallery || !mainImage || !source) return;

    const thumbnails = Array.from(
      gallery.querySelectorAll("[data-ap-gallery-thumbnail]"),
    );
    const normalizedSource = normalizedUrl(source);
    const activeThumbnail = thumbnails.find(
      (thumbnail) =>
        normalizedUrl(thumbnail.dataset.gallerySrc) === normalizedSource,
    );

    mainImage.removeAttribute("srcset");
    mainImage.removeAttribute("sizes");
    mainImage.src = source;
    mainImage.alt =
      activeThumbnail?.dataset.galleryAlt || fallbackAlt || mainImage.alt || "";

    thumbnails.forEach((thumbnail) => {
      thumbnail.setAttribute(
        "aria-pressed",
        thumbnail === activeThumbnail ? "true" : "false",
      );
    });
  }

  function bindGallery(root) {
    const gallery = root.querySelector("[data-ap-gallery]");
    if (!gallery || gallery.dataset.apGalleryBound === "true") return;
    gallery.dataset.apGalleryBound = "true";

    gallery.querySelectorAll("[data-ap-gallery-thumbnail]").forEach(
      (thumbnail) => {
        thumbnail.addEventListener("click", () => {
          setGalleryImage(
            root,
            thumbnail.dataset.gallerySrc,
            thumbnail.dataset.galleryAlt,
          );
        });
      },
    );
  }

  function setVariant(root, variant) {
    const price = root.querySelector("[data-ap-price]");
    const addButton = root.querySelector("[data-ap-add]");
    const addText = root.querySelector("[data-ap-add-text]");
    const layerAddButtons = Array.from(
      document.querySelectorAll("[data-ap-layer-add]"),
    );
    const layerAddTexts = Array.from(
      document.querySelectorAll("[data-ap-layer-text]"),
    );
    const stockMessage = root.querySelector("[data-ap-stock-message]");
    const stockText = root.querySelector("[data-ap-stock-text]");
    const primaryImage = root.querySelector(".ap-pdp__image--primary");
    const stickyAddButton = root.querySelector("[data-ap-sticky-add]");
    const stickyAddText = root.querySelector("[data-ap-sticky-add-text]");
    const stickyPrice = root.querySelector("[data-ap-sticky-price]");
    const stickyVariant = root.querySelector("[data-ap-sticky-variant]");
    const stickyImage = root.querySelector(
      ".ap-pdp__mobile-buybar-product-image",
    );
    const returnsBadge = root.querySelector("[data-ap-returns-badge]");

    const updateStockMessage = (available) => {
      if (!stockMessage || !stockText) return;
      const message = available
        ? stockMessage.dataset.inStockMessage
        : stockMessage.dataset.soldOutMessage;
      stockText.textContent = message || "";
      stockMessage.hidden = !message;
    };

    if (returnsBadge) {
      const compareAtPrice = Number(variant?.compareAtPrice || 0);
      const price = Number(variant?.price || 0);
      returnsBadge.hidden = !variant || compareAtPrice > price;
    }

    root
      .querySelectorAll("[data-ap-variant-id], [data-ap-installment-variant]")
      .forEach((input) => {
        input.value = variant ? variant.id : "";
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    document.querySelectorAll("[data-ap-layer-variant]").forEach((input) => {
      input.value = variant ? variant.id : "";
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });

    if (!variant) {
      if (addButton) addButton.disabled = true;
      if (addText) addText.textContent = "Unavailable";
      if (stickyAddButton) stickyAddButton.disabled = true;
      if (stickyAddText) stickyAddText.textContent = "Unavailable";
      layerAddButtons.forEach((button) => {
        button.disabled = true;
      });
      layerAddTexts.forEach((text) => {
        text.textContent = "Unavailable";
      });
      updateStockMessage(false);
      return;
    }

    if (price) price.textContent = variant.priceCurrency;
    if (stickyPrice) stickyPrice.textContent = variant.priceCurrency;
    if (stickyVariant) stickyVariant.textContent = variant.options.join(" · ");
    if (addButton) addButton.disabled = !variant.available;
    if (stickyAddButton) stickyAddButton.disabled = !variant.available;
    layerAddButtons.forEach((button) => {
      button.disabled = !variant.available;
    });
    if (addText) {
      addText.textContent = variant.available
        ? `${root.dataset.apAddLabel || "Add to bag"} · ${variant.priceFormatted}`
        : (root.dataset.apSoldLabel || "Sold out");
    }
    if (stickyAddText) {
      stickyAddText.textContent = variant.available ? (root.dataset.apAddLabel || "Add to bag") : (root.dataset.apSoldLabel || "Sold out");
    }
    layerAddTexts.forEach((text) => {
      text.textContent = variant.available
        ? `${text.dataset.apLayerLabel || "Add this fragrance"} +`
        : "Sold out";
    });
    updateStockMessage(variant.available);

    if (primaryImage && variant.featuredImage) {
      setGalleryImage(root, variant.featuredImage, primaryImage.alt);
    }
    if (stickyImage && variant.featuredImage) {
      stickyImage.removeAttribute("srcset");
      stickyImage.src = variant.featuredImage;
    }

    const productUrl = root.dataset.productUrl;
    if (productUrl && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState(
        {},
        "",
        `${url.pathname}${url.search}${url.hash}`,
      );
    }

    root.dispatchEvent(
      new CustomEvent("ap:variant:change", {
        bubbles: true,
        detail: { variant },
      }),
    );
  }

  function bindQuantity(root) {
    const input = root.querySelector("[data-ap-qty-input]");
    const minus = root.querySelector("[data-ap-qty-minus]");
    const plus = root.querySelector("[data-ap-qty-plus]");
    if (!input || !minus || !plus) return;

    const normalize = (value) =>
      Math.max(Number(input.min) || 1, Number.parseInt(value, 10) || 1);
    minus.addEventListener("click", () => {
      input.value = normalize(Number(input.value) - 1);
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
    plus.addEventListener("click", () => {
      input.value = normalize(Number(input.value) + 1);
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
    input.addEventListener("change", () => {
      input.value = normalize(input.value);
    });
  }

  function bindMobileBuybar(root, variants) {
    const buybar = root.querySelector("[data-ap-mobile-buybar]");
    const stickyAddButton = root.querySelector("[data-ap-sticky-add]");
    const stickyAddText = root.querySelector("[data-ap-sticky-add-text]");
    const addButton = root.querySelector("[data-ap-add]");
    if (!buybar || !stickyAddButton || !addButton) return;

    let submitting = false;
    let resetTimer = 0;

    const setSubmitting = (isSubmitting) => {
      submitting = isSubmitting;
      root.classList.toggle("ap-pdp--submitting", isSubmitting);
      addButton.toggleAttribute("aria-busy", isSubmitting);
      stickyAddButton.toggleAttribute("aria-busy", isSubmitting);

      if (isSubmitting) {
        addButton.disabled = true;
        stickyAddButton.disabled = true;
        if (stickyAddText) {
          stickyAddText.textContent =
            stickyAddButton.dataset.apLoadingLabel || "Adding…";
        }
        window.clearTimeout(resetTimer);
        resetTimer = window.setTimeout(() => setSubmitting(false), 20000);
        return;
      }

      window.clearTimeout(resetTimer);
      const currentVariant = findVariant(root, variants);
      setVariant(root, currentVariant);
    };

    addButton.addEventListener(
      "click",
      (event) => {
        if (submitting) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        if (!addButton.disabled) setSubmitting(true);
      },
      { capture: true },
    );

    stickyAddButton.addEventListener("click", () => {
      if (!submitting && !addButton.disabled) addButton.click();
    });

    const finishSubmitting = () => {
      if (!submitting) return;
      window.setTimeout(() => setSubmitting(false), 0);
    };
    document.addEventListener("eurus:product:added", finishSubmitting);
    document.addEventListener("eurus:product:add-failed", finishSubmitting);

    let frame = 0;
    const update = () => {
      frame = 0;
      const addButtonRect = addButton.getBoundingClientRect();
      const footer = document.querySelector(".section-footer");
      const footerReached = footer
        ? footer.getBoundingClientRect().top <= window.innerHeight
        : false;
      const visible = addButtonRect.bottom < 0 && !footerReached;
      buybar.classList.toggle("is-visible", visible);
      buybar.setAttribute("aria-hidden", visible ? "false" : "true");
    };
    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    requestUpdate();
  }

  function bindPyramid(pyramid) {
    if (!pyramid || pyramid.dataset.apPyramidReady === "true") return;
    pyramid.dataset.apPyramidReady = "true";

    const sticky = pyramid.querySelector(".ap-pdp__composition-sticky");
    const header = pyramid.querySelector("[data-ap-pyramid-header]");
    const stageItems = Array.from(
      pyramid.querySelectorAll("[data-ap-pyramid-tier]"),
    );
    const connectorItems = Array.from(
      pyramid.querySelectorAll("[data-ap-pyramid-connector]"),
    );
    const iconItems = Array.from(
      pyramid.querySelectorAll("[data-ap-pyramid-icon]"),
    );
    const stagePartItems = Array.from(
      pyramid.querySelectorAll("[data-ap-stage-part]"),
    );
    const allAnimatedItems = [
      ...stageItems,
      ...connectorItems,
      ...iconItems,
      ...stagePartItems,
    ];
    if (!sticky || !stageItems.length) return;

    const buildOrder = {};
    stageItems.forEach((item, index) => {
      buildOrder[item.dataset.apPyramidSource] = index;
    });
    allAnimatedItems.forEach((item) => {
      const source = item.dataset.apPyramidSource;
      item.style.setProperty("--ap-stage-index", buildOrder[source] ?? 0);
    });

    const reducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reducedMotion) {
      pyramid.classList.add("ap-pdp__pyramid--reduced");
      allAnimatedItems.forEach((item) => {
        item.style.opacity = "1";
        item.style.transform = "none";
        item.style.filter = "none";
      });
      return;
    }

    const clamp = (value) => Math.min(1, Math.max(0, value));
    const ease = (value) => 1 - Math.pow(1 - value, 3);
    let frame = 0;

    const update = () => {
      frame = 0;
      const rect = pyramid.getBoundingClientRect();
      const scrollDistance = Math.max(
        1,
        pyramid.offsetHeight - window.innerHeight,
      );
      const progress = clamp(-rect.top / scrollDistance);
      const entryProgress = ease(
        clamp((window.innerHeight * 0.88 - rect.top) / (window.innerHeight * 0.42)),
      );

      if (header) {
        header.style.opacity = entryProgress.toFixed(3);
        header.style.transform = `translateY(${((1 - entryProgress) * 14).toFixed(2)}px)`;
      }

      stageItems.forEach((item) => {
        const source = item.dataset.apPyramidSource;
        const index = buildOrder[source] ?? 0;
        const localProgress = ease(clamp((progress - index * 0.29) / 0.24));
        const distance = 1 - localProgress;

        item.style.opacity = (0.68 + localProgress * 0.32).toFixed(3);
        item.style.transform = `translateY(${(distance * 16).toFixed(2)}px)`;
        item.style.filter = `blur(${(distance * 1.5).toFixed(2)}px)`;
      });

      connectorItems.forEach((item) => {
        const index = buildOrder[item.dataset.apPyramidSource] ?? 1;
        const connectorProgress = ease(
          clamp((progress - (index * 0.29 - 0.1)) / 0.18),
        );
        item.style.opacity = connectorProgress.toFixed(3);
        item.style.transform = `scaleY(${connectorProgress.toFixed(3)})`;
      });

      iconItems.forEach((item) => {
        const index = buildOrder[item.dataset.apPyramidSource] ?? 0;
        const stageProgress = clamp((progress - index * 0.29) / 0.24);
        const iconIndex = Number.parseInt(item.dataset.apIconIndex, 10) || 0;
        const iconProgress = ease(
          clamp((stageProgress - 0.12 - iconIndex * 0.12) / 0.46),
        );
        const distance = 1 - iconProgress;

        item.style.opacity = (0.68 + iconProgress * 0.32).toFixed(3);
        item.style.transform = `translateY(${(distance * 8).toFixed(2)}px) scale(${(0.9 + iconProgress * 0.1).toFixed(4)})`;
      });

      stagePartItems.forEach((item) => {
        const index = buildOrder[item.dataset.apPyramidSource] ?? 0;
        const stageProgress = clamp((progress - index * 0.29) / 0.24);
        const partIndex = Number.parseInt(item.dataset.apPartIndex, 10) || 0;
        const partProgress = ease(
          clamp((stageProgress - 0.05 - partIndex * 0.1) / 0.58),
        );
        const distance = 1 - partProgress;

        item.style.opacity = (0.74 + partProgress * 0.26).toFixed(3);
        item.style.transform = `translateY(${(distance * 6).toFixed(2)}px)`;
      });

      pyramid.style.setProperty("--ap-pyramid-progress", progress.toFixed(3));
    };

    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    pyramid.classList.add("ap-pdp__pyramid--ready", "ap-pdp__pyramid--scroll");
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    requestUpdate();
  }

  function init(root) {
    if (!root || root.dataset.apProductReady === "true") return;
    root.dataset.apProductReady = "true";

    const variants = parseVariants(root);
    root.querySelectorAll("[data-ap-option-value]").forEach((input) => {
      input.addEventListener("change", () =>
        setVariant(root, findVariant(root, variants)),
      );
    });

    const addButton = root.querySelector("[data-ap-add]");
    if (addButton) {
      addButton.addEventListener(
        "click",
        () => {
          const currentVariant = findVariant(root, variants);
          if (currentVariant) setVariant(root, currentVariant);
        },
        { capture: true },
      );
    }

    bindQuantity(root);
    bindGallery(root);
    bindMobileBuybar(root, variants);
    const initialVariant =
      variants.find(
        (variant) =>
          String(variant.id) ===
          String(root.querySelector("[data-ap-variant-id]")?.value),
      ) || findVariant(root, variants);
    if (initialVariant) setVariant(root, initialVariant);
  }

  function initAll(scope = document) {
    if (scope.matches?.(PRODUCT_SELECTOR)) init(scope);
    scope.querySelectorAll?.(PRODUCT_SELECTOR).forEach(init);
    if (scope.matches?.(PYRAMID_SELECTOR)) bindPyramid(scope);
    scope.querySelectorAll?.(PYRAMID_SELECTOR).forEach(bindPyramid);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) =>
    initAll(event.target),
  );
})();
